import { useRef, useState } from 'react';
import type { TouchEvent as ReactTouchEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { SurveyBanner } from './SurveyBanner';
import {
  Mascot,
  MetricFatigueIcon,
  SortVerticalIcon,
  UserSpeakIcon,
} from '../../../components';
import { useOnboarding } from '../context';
import { ESTIMATED_MINUTES, RECORD_TESTS } from '../recordTests';
import { ensureAnalySessionId } from '../server';

import type { RecordTest } from '../recordTests';

/**
 * 음성 진단 안내 팝업(덱 p5) — 진단에 들어가기 직전의 2장짜리 안내.
 *
 * 1장: 어떤 테스트를 하는지 · 2장: 얼마나 걸리는지 + "잘 부르지 않아도 괜찮아요".
 * 목록과 개수는 **`RECORD_TESTS`에서 센다** — 문장에 "세 가지"를 굳혀 두면 진단이 늘 때
 * 화면에는 두 줄, 문장에는 세 가지가 남는다.
 *
 * 진행(→ 녹음)은 이 화면의 "진단하기"가 한다(StepShell 기본 CTA는 숨김).
 *
 * 그 버튼이 **서버 진단 루트를 여는 자리**이기도 하다. 기다리지 않는다 — 루트가 늦거나 못
 * 서도 진단은 그대로 열려야 하고, 못 선 경우는 첫 테이크 제출이 한 번 더 시도한다.
 * 여기서 여는 이유는 중간 이탈도 기록에 남기기 위해서다(테이크마다 제출한다).
 */

const SLIDES = 2;
/** 44px 터치 타깃 — 원형 화살표가 가장 작아지기 쉬운 자리다. */
const ARROW =
  'flex h-11 w-11 items-center justify-center rounded-full border border-[var(--bk-100)] bg-[hsl(var(--surface-lowest))]';

/**
 * 시안 `760:29671`의 목록 아이콘 — 기본 발성은 파형(레드), 음역은 위아래 화살표(오렌지),
 * 노래는 말하는 사람(레드). 시안은 저음·고음을 한 줄로 묶지만 우리는 두 줄이라 같은
 * 화살표를 나눠 쓴다. 색은 아이콘 SVG가 갖는다(생성기가 의미 있는 색을 남긴다).
 */
const TEST_ICON: Record<RecordTest['id'], typeof UserSpeakIcon> = {
  sustain: MetricFatigueIcon,
  low: SortVerticalIcon,
  high: SortVerticalIcon,
  song: UserSpeakIcon,
};

const TestList = () => {
  const { t } = useTranslation();
  return (
    <ul className="mt-8 flex flex-col gap-5">
      {RECORD_TESTS.map((test) => {
        const Icon = TEST_ICON[test.id];
        return (
          <li key={test.id} className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bk-50"
            >
              <Icon className="h-[22px] w-[22px]" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-base font-semibold tracking-[-0.5px]">
                {t(test.titleKey)}
              </span>
              <span className="text-sm text-bk-600">{t(test.summaryKey)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const DurationSlide = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center">
      <SurveyBanner className="mt-6 w-full">
        {t('web.diagnosis.intro.durationSubtitle')}
      </SurveyBanner>
      <Mascot className="mt-8 w-[160px]" />
    </div>
  );
};

/** 스와이프로 칠 최소 거리(px) — 이보다 짧으면 탭이 미끄러진 것으로 본다. */
const SWIPE_PX = 40;

export const DiagnosisIntroStep = () => {
  const { t } = useTranslation();
  const { next } = useOnboarding();
  const [slide, setSlide] = useState(0);
  /**
   * 화살표만으로는 WebView 에서 이 화면이 넘길 수 있는 것인지 안 보인다 — 점 두 개가
   * 있는 화면에서 사람이 먼저 하는 동작은 스와이프다. 화살표는 그대로 둔다(접근성).
   */
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: ReactTouchEvent<HTMLDivElement>): void => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: ReactTouchEvent<HTMLDivElement>): void => {
    const from = touchX.current;
    touchX.current = null;
    const to = e.changedTouches[0]?.clientX;
    if (from == null || to == null || Math.abs(to - from) < SWIPE_PX) {
      return;
    }
    setSlide((cur) =>
      Math.min(Math.max(cur + (to < from ? 1 : -1), 0), SLIDES - 1),
    );
  };

  return (
    <div
      className="flex flex-1 flex-col"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* 나가는 문은 셸 헤더가 갖는다 — 여기서 또 그리면 화면에 `×`가 둘이 된다. */}
      <div
        className="flex justify-center gap-2"
        role="status"
        aria-label={t('web.diagnosis.intro.slides', {
          current: slide + 1,
          total: SLIDES,
        })}
      >
        {Array.from({ length: SLIDES }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-2 w-2 rounded-full',
              i === slide ? 'bg-primary' : 'bg-muted',
            )}
          />
        ))}
      </div>

      <h2 className="mt-8 whitespace-pre-line break-keep text-center text-[22px] font-semibold leading-[1.5] tracking-[-0.11px] text-[hsl(var(--title))]">
        {slide === 0
          ? t('web.diagnosis.intro.testsTitle', { value: RECORD_TESTS.length })
          : t('web.diagnosis.intro.durationTitle', {
              value: ESTIMATED_MINUTES,
            })}
      </h2>

      {slide === 0 ? <TestList /> : <DurationSlide />}

      {/* 화살표는 두 장에서 **같은 자리**에 놓여야 한다. 슬라이드가 남은 높이를 먹으면
          한 장에서는 목록 바로 아래, 다른 장에서는 화면 맨 밑으로 내려간다. */}
      <div className="mt-8 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => setSlide(slide - 1)}
          disabled={slide === 0}
          aria-label={t('web.diagnosis.intro.prev')}
          className={cn(ARROW, slide === 0 && 'opacity-40')}
        >
          <ChevronLeft
            className={cn(
              'h-5 w-5',
              slide === 0 ? 'text-[var(--bk-400)]' : 'text-primary',
            )}
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          onClick={() => setSlide(slide + 1)}
          disabled={slide >= SLIDES - 1}
          aria-label={t('web.diagnosis.intro.next')}
          className={cn(ARROW, slide >= SLIDES - 1 && 'opacity-40')}
        >
          <ChevronRight
            className={cn(
              'h-5 w-5',
              slide >= SLIDES - 1 ? 'text-[var(--bk-400)]' : 'text-primary',
            )}
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="min-h-6 flex-1" aria-hidden="true" />

      <button
        type="button"
        onClick={() => {
          void ensureAnalySessionId();
          next();
        }}
        className="mb-8 h-[50px] w-full rounded-full bg-primary text-base font-bold tracking-[0.08px] text-primary-foreground"
      >
        {t('web.diagnosis.intro.cta')}
      </button>
    </div>
  );
};
